// SpecialFee.tsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  DollarSign,
  Info,
  Tag,
  Package,
  CheckCircle2,
  Users,
  Search as SearchIcon,
  Building2,
  X,
  Check
} from "lucide-react";
import Select, { components, MultiValue } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import TaxRuleSelect from "components/services/taxrulesandTaxgroupsui";
import da from "assets/libs/moment/src/locale/da";
import { sendTriggerEmails } from "utils/emailService";
import { getAuthCookie } from "../../utils/authCookies";
import { renderSignagePersonOptionLabel } from "components/proejctsandquoteapicalls";

type FeeLimitTo = "none" | "agent" | "brokerage";
type GroupsLimitTo = "none" | "groups";
type AvailableOn = "" | "cart" | "checkout" | "both Cart and Checkout";

// Simple types matching SignageAddInstallation
type Agent = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone1: string;
};

type Brokerage = {
  id: string;
  name?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone1: string;
  firmDetails?: {
    companyName: string;
  };
};

// Select2 type for brokerages
type BrokerageOption = {
  value: string;
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone1: string;
  firmDetails?: {
    companyName: string;
  };
};

type Group = { _id: string; name: string };

const DropdownIndicator = (props: any) => {
  if (props.selectProps.isDisabled) return null;
  return (
    <components.DropdownIndicator {...props}>
      <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
    </components.DropdownIndicator>
  );
};

// Custom Option component with checkmarks for selected items
const CustomOption = (props: any) => {
  const { isSelected, children, innerProps, data, selectProps } = props;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (data.value && data.value !== "") {
      // Get the current selected values from the parent component
      const currentSelected = selectProps.selectedValues || [];

      if (isSelected) {
        // Remove from selected
        const newSelected = currentSelected.filter((id: string) => id !== data.value);
        selectProps.onSelectionChange?.(newSelected, data, false); // false = removing
      } else {
        // Add to selected
        const newSelected = [...currentSelected, data.value];
        selectProps.onSelectionChange?.(newSelected, data, true); // true = adding
      }
    }
  };

  return (
    <components.Option {...props}>
      <div className="d-flex align-items-start gap-2" onClick={handleClick} style={{ cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={isSelected}
          readOnly
          className="form-check-input"
          style={{ margin: "3px 0 0 0", pointerEvents: 'none' }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          {String(data?.email || "").trim()
            ? renderSignagePersonOptionLabel(
                { label: String(data?.label || children || ""), email: data.email },
                { context: "menu" }
              )
            : children}
        </div>
      </div>
    </components.Option>
  );
};

const customSelectStyles = {
  /* Root container: default react-select sets pointerEvents:none when disabled, which hides not-allowed cursor */
  container: (provided: any, state: any) => ({
    ...provided,
    cursor: state.isDisabled ? "not-allowed" : "default",
    pointerEvents: state.isDisabled ? "auto" : provided.pointerEvents,
  }),
  /* hide all indicators when disabled */
  indicatorsContainer: (provided: any, state: any) => ({
    ...provided,
    display: state.isDisabled ? "none" : "flex",
  }),
  dropdownIndicator: (provided: any, state: any) => ({
    ...provided,
    display: state.isDisabled ? "none" : "flex",
  }),
  indicatorSeparator: () => ({ display: "none" }),

  /* keep bg and cursor right; do NOT fade */
  control: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isDisabled ? "#f2f2f2" : provided.backgroundColor,
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    opacity: 1,
  }),
  /* ——— make text BLACK when disabled ——— */
  singleValue: (provided: any, state: any) => ({
    ...provided,
    color: state.isDisabled ? "#000" : provided.color,
    cursor: state.isDisabled ? "not-allowed" : provided.cursor,
  }),
  valueContainer: (provided: any, state: any) => ({
    ...provided,
    color: state.isDisabled ? "#000" : provided.color,
    cursor: state.isDisabled ? "not-allowed" : provided.cursor,
  }),
  input: (provided: any, state: any) => ({
    ...provided,
    color: state.isDisabled ? "#000" : provided.color,
    WebkitTextFillColor: state.isDisabled ? "#000" : undefined, // Safari
  }),
  placeholder: (provided: any, state: any) => ({
    ...provided,
    /* if field has no value, keep placeholder readable too */
    color: state.isDisabled ? "#000" : provided.color,
  }),

  /* if you ever use isMulti, keep chips readable */
  multiValue: (p: any, s: any) => ({
    ...p,
    backgroundColor: s.isDisabled ? "#e5e7eb" : p.backgroundColor,
  }),
  multiValueLabel: (p: any, s: any) => ({
    ...p,
    color: s.isDisabled ? "#000" : p.color,
  }),
  multiValueRemove: (p: any, s: any) => ({
    ...p,
    display: s.isDisabled ? "none" : p.display,
  }),
  option: (provided: any) => ({
    ...provided,
    whiteSpace: "normal",
    lineHeight: 1.35,
    paddingTop: 8,
    paddingBottom: 8,
  }),
};

const COLORS = {
  headerBg: "#d0fae5",
  headerIcon: "#009966",

  basicBg: "#dbeafe",
  basicIcon: "#155dfc",

  configBg: "#dcfce7",
  configIcon: "#00a63e",

  feeBg: "#f3e8ff",
  feeIcon: "#9810fa",

  statusBg: "#f6f3f4",
  statusIcon: "#4a5565",

  statusActiveBg: "#00a63e",
  statusInactiveBg: "#cbced4",

  requiredYesBg: "#f54a00",
  requiredNoBg: "#cbced4",
};

// Pagination constants
const PAGE_SIZE = 10;

export default function SpecialFee() {
  const { feeId } = useParams<{ feeId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';
  // Determine mode based on URL path
  const pathname = location.pathname;
  const isAdd = pathname.includes('/add-special-fee');
  const isEdit = pathname.includes('/edit-special-fee');
  const isView = pathname.includes('/view-special-fee');

  // Determine if fields should be disabled (view mode)
  const isDisabled = isView;

  // Get user info from localStorage
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const organizationId = getAuthCookie("organizationId");
  // Helper function to normalize IDs
  const normalizeId = (row: any) => String(row?.id ?? row?._id ?? "");

  // ----- Top selects -----
  const limitToOptions = [
    { value: "none", label: "No limitation" },
    { value: "agent", label: "Agents" },
    { value: "brokerage", label: "Brokerages" },
  ];
  const limitToGroupsOptions = [
    { value: "none", label: "No groups limitation" },
    { value: "groups", label: "Groups" },
  ];
  const availableOnOptions = [
    { value: "cart", label: "Cart" },
    { value: "checkout", label: "Checkout Page" },
    { value: "both Cart and Checkout", label: "Both Cart & Checkout" },
  ];

  // ----- Internal CSS -----
  const styles = useMemo(
    () => `
    .sf-card { border-radius: 14px; }
    .sf-card-body { padding: 1.25rem 1.25rem 1rem; }
    .sf-header-icon, .sf-section-icon { display:flex; align-items:center; justify-content:center; }
    .sf-header-icon { width: 36px; height: 36px; border-radius: 10px; }
    .sf-section-icon { width: 28px; height: 28px; border-radius: 8px; }
    .sf-divider { border-top: 1px solid var(--bs-border-color); opacity:.9; }
    .sf-input-lg { height: 44px; }
    .sf-input-prefix { 
      min-width: 44px; 
      background-color: #f5f6fa;
      border-color: #c9ced6;
      border-width: 2px;
      border-style: solid;
      border-radius: 10px 0 0 10px;
      color: #111827;
    }
    .sf-help { color: var(--bs-secondary-color); font-size: .875rem; }
    .sf-footer { padding-top: .75rem; padding-bottom: .25rem; }

    /* Switch overrides */
    #statusSwitch.form-check-input:checked { background-color: ${COLORS.statusActiveBg}; border-color: ${COLORS.statusActiveBg}; }
    #statusSwitch.form-check-input:not(:checked) { background-color: ${COLORS.statusInactiveBg}; border-color: ${COLORS.statusInactiveBg}; }
    #requiredSwitch.form-check-input:checked { background-color: ${COLORS.requiredYesBg}; border-color: ${COLORS.requiredYesBg}; }
    #requiredSwitch.form-check-input:not(:checked) { background-color: ${COLORS.requiredNoBg}; border-color: ${COLORS.requiredNoBg}; }
    #statusSwitch.form-check-input:focus, #requiredSwitch.form-check-input:focus { box-shadow: 0 0 0 .2rem rgba(0,0,0,0.04); }
    .check{ width:40px !important; height:20px; }

    /* react-select disabled: not-allowed cursor (mutual exclusivity + view mode) */
    .sf-special-fee [class*="__control--is-disabled"],
    .sf-special-fee [class*="__control--is-disabled"] * {
      cursor: not-allowed !important;
    }

    /* Inputs */
    .sf-form-control {
      width: 100%;
      height: 39px;
      border-radius: 10px;
      padding: 10px 12px;
      border-width: 2px;
      border-style: solid;
      border-color: #c9ced6;
      background-color: #f5f6fa;
      outline: none;
      transition: all .2s ease-in-out;
      box-shadow: none;
      color: #111827;
    }
    .sf-form-control:hover { border-color: #9ca3af; background-color: #fafafa; }
    .sf-form-control:focus { border-color: #9ca3af; background-color: #fafafa; box-shadow: none; }
    .sf-form-control.is-invalid { border-color: #747981; }
    .input-group .sf-form-control { border-radius: 0 10px 10px 0; }

    /* Amount input field specific styling */
    .sf-amount-input {
      width: 80%;
      height: 39px;
      border-radius: 0 10px 10px 0;
      padding: 10px 12px;
      border-width: 2px;
      border-style: solid;
      border-color: #c9ced6;
      background-color: #f5f6fa;
      outline: none;
      transition: all .2s ease-in-out;
      box-shadow: none;
      color: #111827;
    }
    
    .sf-amount-input:hover {
      border-color: #9ca3af;
      background-color: #fafafa;
    }
    
    .sf-amount-input:focus {
      border-color: #9ca3af;
      background-color: #fafafa;
      box-shadow: none;
      border-left: none;
    }
    
    .sf-amount-input.is-invalid {
      border-color: #747981;
    }

    /* Search + list */
    .sf-search-wrap { position:relative; }
    .sf-search-wrap .sf-search-icon { position:absolute; left:10px; top:50%; transform:translateY(-50%); }
    .sf-search-input { padding-left: 36px; }
    .sf-list { max-height: 240px; overflow:auto; border: 2px solid #e7eaee; border-radius: 12px; }
    .sf-list .list-group-item { border: none; }
    .sf-list .form-check-input { width: 16px; height: 16px; }

    /* Fixed-height chips bar (horizontal scroll) */
    .sf-chips-bar {
      min-height: 44px;
      max-height: 44px;
      overflow-x: auto;
      overflow-y: hidden;
      white-space: nowrap;
      border: 2px solid #e7eaee;
      border-radius: 12px;
      background: #f5f6fa;
      padding: 6px 8px;
    }
    .sf-chips-bar::-webkit-scrollbar { height: 8px; }
    .sf-chips-bar::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:999px; }

    .sf-chip {
      display:inline-flex; align-items:center; gap:.35rem;
      padding:.35rem .6rem; border-radius:999px;
      background:#e8eeff; color:#1e3a8a; font-weight:500;
      margin-right: .5rem;
    }
    .sf-chip .btn-close { width:.6rem; height:.6rem; margin-left:.15rem; }
  `,
    []
  );

  // ----- Form state will be managed by Formik -----

  // Dynamic data state (ready for API integration)
  const [agents, setAgents] = useState<Agent[]>([]);
  const [brokerages, setBrokerages] = useState<Brokerage[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);

  // State to track selected brokerages for AsyncPaginate (multi-select)
  const [selectedBrokerageOptions, setSelectedBrokerageOptions] = useState<BrokerageOption[]>([]);

  const [loading, setLoading] = useState({
    agents: false,
    brokerages: false,
    groups: false,
    submit: false,
    fetch: false
  });

  // Error and success states
  const [apiError, setApiError] = useState<string>("");

  // Formik setup
  const formik = useFormik({
    initialValues: {
      name: "",
      limitTo: "none" as FeeLimitTo,
      limitToGroups: "none" as GroupsLimitTo,
      amount: "",
      taxable: false,
      tax_rule_id: "",                 // "group-<id>" | "rule-<id>" | ""
      tax_group: null as any | null,   // full group object
      tax_rule: null as any | null,    // full rule object
      availableOn: "" as AvailableOn,
      status: true,
      required: false,
      selectedAgents: [] as string[],
      selectedBrokerages: [] as string[],
      selectedGroups: [] as string[],
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      amount: Yup.string().required("Amount is required"),
      availableOn: Yup.string().required("Please select availability"),
      //       tax_rule_id: Yup.string().when("taxable", {
      //   is: true, then: (s) => s.required("Select a tax rule"),
      //   otherwise: (s) => s.notRequired(),
      // }),
    }),
    onSubmit: async (values, { setSubmitting, setStatus }) => {
      try {
        setSubmitting(true);
        setStatus(undefined);

        const groupId =
          values.taxable ? (values.tax_group?._id ?? values.tax_group?.id ?? null) : null;

        const ruleId =
          values.taxable && !groupId
            ? (values.tax_rule?._id ?? values.tax_rule?.id ?? null)
            : null;
        // Prepare the payload according to SpecialFeeWritePayload
        const limitToAgents =
          values.limitTo === "agent"
            ? (values.selectedAgents || []).map(String).filter(Boolean)
            : [];
        const limitToBrokerages =
          values.limitTo === "brokerage"
            ? (values.selectedBrokerages || []).map(String).filter(Boolean)
            : [];
        const limitToGroups =
          values.limitToGroups === "groups"
            ? (values.selectedGroups || []).map(String).filter(Boolean)
            : [];

        const payload = {
          name: values.name,
          limit_to_agents: limitToAgents,
          limit_to_brokerages: limitToBrokerages,
          limit_to_groups: limitToGroups,
          agent_no_limit: values.limitTo !== "agent",
          brokerage_firm_no_limit: values.limitTo !== "brokerage",
          group_no_limit: values.limitToGroups !== "groups",
          amount: parseFloat(values.amount || "0"),
          taxable: !!values.taxable,
          ...(groupId ? { tax_group_id: groupId } : {}),
          ...(ruleId ? { tax_rule_id: ruleId } : {}),
          available_on: values.availableOn === "both Cart and Checkout"
            ? ["cart", "checkout"]
            : values.availableOn
              ? [values.availableOn]
              : [],
          required: values.required,
          status: values.status,
        };

        if (isEdit) {
          // Update API call - PUT request
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/special-fees/${feeId}`,
            {
              method: "PATCH",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(payload),
            }
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
              (typeof errorData?.error === "string" && errorData.error) ||
              (typeof errorData?.message === "string" && errorData.message) ||
              `Update failed (${response.status})`
            );
          }

          const data = await response.json();
          showSuccess("Special fee updated successfully");
        } else {
          // Create API call - POST request
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/special-fees`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(payload),
            }
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
              (typeof errorData?.error === "string" && errorData.error) ||
              (typeof errorData?.message === "string" && errorData.message) ||
              `Create failed (${response.status})`
            );
          }

          const data = await response.json();
          showSuccess("Special fee created successfully");
        }

        // Reset form after successful submission
        formik.resetForm();

        // Navigate back to special fees list
        navigate(`${baseRoute}/specialfees`);

      } catch (error: any) {
        console.error("Error submitting special fee:", error);
        setStatus({ error: error.message || "Failed to submit special fee" });
        showError(error.message || "Failed to submit special fee");
      } finally {
        setSubmitting(false);
      }
    },
  });

  // API function for fetching groups
  const fetchGroups = async () => {
    try {
      setLoading(prev => ({ ...prev, groups: true }));
      setApiError("");

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/customer-groups`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch groups");
      }

      const data: Group[] = await response.json();
      setGroups(data || []);
    } catch (err) {
      console.error("Error fetching groups:", err);
      setApiError(err instanceof Error ? err.message : "Failed to fetch groups");
      showError("Failed to fetch groups. Please try again.");
    } finally {
      setLoading(prev => ({ ...prev, groups: false }));
    }
  };

  // Agent Option type for AsyncPaginate
  type AgentOption = {
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone1: string;
  };

  const effectiveOrganizationId = React.useMemo(() => {
    return String(organizationId || "").trim();
  }, [organizationId]);

const emptyAsyncResult = (page: number) => ({
  options: [],
  hasMore: false,
  additional: { page },
});

  /** Agents list: customer-groups users-created-or-linked (replaces /users/signage/.../agents/select2). */
  const mapUsersCreatedOrLinkedRowToAgentOption = (u: any): AgentOption => {
    // Backend expects organization_memberships._id in limit_to_agents
    const value = String(
      u?.membership_id ?? u?.membershipId ?? u?.value ?? normalizeId(u) ?? ""
    ).trim();
    const label = String(u?.label ?? "").trim() || "(no name)";
    const parts = label.split(/\s+/).filter(Boolean);
    return {
      value,
      label,
      firstName: parts[0] || "",
      lastName: parts.slice(1).join(" ") || "",
      email: typeof u?.email === "string" ? u.email : "",
      phone1: typeof u?.phone1 === "string" ? u.phone1 : "",
    };
  };

  const mapUsersCreatedOrLinkedRowToAgent = (u: any): Agent => {
    const o = mapUsersCreatedOrLinkedRowToAgentOption(u);
    return {
      id: o.value,
      firstName: o.firstName,
      lastName: o.lastName,
      email: o.email,
      phone1: o.phone1,
    };
  };

  const buildAgentsUsersCreatedOrLinkedUrl = (
    orgId: string,
    page: number,
    limit: number,
    query: string
  ) => {
    const params = new URLSearchParams();
    params.set("license_type", "AGENT,BROKERAGE_AGENT");
    params.set("page", String(page));
    params.set("limit", String(limit));
    params.set("query", query.trim());
    const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
    return `${base}/customer-groups/organizations/${encodeURIComponent(orgId)}/users-created-or-linked?${params.toString()}`;
  };

  const paginationHasMoreFromUsersCreatedOrLinked = (
    json: any,
    page: number,
    limit: number,
    rowCount: number
  ): boolean => {
    const lim = Number(json?.limit ?? limit) || limit;
    const total = Number(json?.total);
    const totalPages = Number(json?.totalPages);
    if (Number.isFinite(totalPages) && totalPages > 0) {
      return page < totalPages;
    }
    if (Number.isFinite(total) && total >= 0) {
      return page * lim < total;
    }
    return rowCount >= lim;
  };

  // ===== API: Agents via customer-groups users-created-or-linked (AsyncPaginate) =====
const PAGE_SIZE_AGENT = 20;

const loadAgentOptions: LoadOptions<AgentOption, any, { page: number }> = React.useCallback(
  async (inputValue, _loadedOptions, additional) => {
    const page = additional?.page ?? 1;
    const q = (inputValue || "").trim();

    if (!token || !effectiveOrganizationId) return emptyAsyncResult(1);

    try {
      const url = buildAgentsUsersCreatedOrLinkedUrl(
        effectiveOrganizationId,
        page,
        PAGE_SIZE_AGENT,
        q
      );

      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) return emptyAsyncResult(page);

      const json = await response.json();
      const list = Array.isArray(json) ? json : json.data ?? json.results ?? [];

      const options: AgentOption[] = list
        .map((u: any) => mapUsersCreatedOrLinkedRowToAgentOption(u))
        .filter((opt: AgentOption) => Boolean(opt.value));

      const hasMore = paginationHasMoreFromUsersCreatedOrLinked(json, page, PAGE_SIZE_AGENT, options.length);

      return { options, hasMore, additional: { page: page + 1 } };
    } catch (err) {
      console.error("Failed to load agent options:", err);
      return emptyAsyncResult(page);
    }
  },
  [token, effectiveOrganizationId]
);


  // Brokerages with Select2 (AsyncPaginate)
const PAGE_SIZE_BROKERAGE = 10; // use your PAGE_SIZE if you already have one

const loadBrokerageOptions: LoadOptions<BrokerageOption, any, { page: number }> = React.useCallback(
  async (inputValue, _loadedOptions, additional) => {
    const page = additional?.page ?? 1;
    const q = (inputValue || "").trim();

    if (!token || !effectiveOrganizationId) return emptyAsyncResult(1);

    try {
      const params = new URLSearchParams({
        licenseType: "BROKERAGE_FIRM",
        signageFirmID: effectiveOrganizationId,
        page: String(page),
        limit: String(PAGE_SIZE_BROKERAGE),
      });
      if (q) params.set("search", q);

      const url = `${process.env.REACT_APP_API_URL}/organization-profiles/search?${params.toString()}`;

      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) return emptyAsyncResult(page);

      const data = await response.json();
      const list = Array.isArray(data) ? data : data.data ?? data.results ?? [];

      const options: BrokerageOption[] = list
        .map((u: any) => {
          const label =
            String(
              u?.firmDetails?.companyName ||
                u?.companyName ||
                u?.text ||
                u?.label ||
                `${u?.firstName || ""} ${u?.lastName || ""}`.trim() ||
                ""
            ).trim() || "(no name)";
          return {
            // Backend expects organizations._id in limit_to_brokerages
            value: String(
              u?.organization_id ?? u?.organizationId ?? normalizeId(u) ?? ""
            ).trim(),
            label,
            firstName: u?.firstName || "",
            lastName: u?.lastName || "",
            email: u?.email || "",
            phone1: u?.phone1 || "",
            firmDetails: {
              companyName:
                String(u?.firmDetails?.companyName || u?.companyName || label).trim() ||
                label,
            },
          };
        })
        .filter((opt: BrokerageOption) => Boolean(opt.value));

      const hasMore = Array.isArray(data)
        ? options.length === PAGE_SIZE_BROKERAGE
        : data?.pagination?.more ??
          (data?.total
            ? page * (data?.limit ?? PAGE_SIZE_BROKERAGE) < data.total
            : options.length === PAGE_SIZE_BROKERAGE);

      return { options, hasMore, additional: { page: page + 1 } };
    } catch (err) {
      console.error("Failed to load brokerage options:", err);
      return emptyAsyncResult(page);
    }
  },
  [token, effectiveOrganizationId]
);


  // Fetch groups when limitToGroups changes to "groups" or when we have selected groups in edit/view mode
  useEffect(() => {
    if ((formik.values.limitToGroups === "groups" && groups.length === 0) ||
      (formik.values.selectedGroups.length > 0 && groups.length === 0)) {
      fetchGroups();
    }
  }, [formik.values.limitToGroups, formik.values.selectedGroups.length]);

  // Note: Agents are now loaded via AsyncPaginate when needed
  // Agent data for chip display is loaded in fetchSpecialFee for edit/view mode

  // Note: Brokerages are now loaded via AsyncPaginate when needed

  // Fetch special fee data for edit mode
  const fetchSpecialFee = async (feeId: string) => {
    try {
      setLoading(prev => ({ ...prev, fetch: true }));
      setApiError("");

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/special-fees/${feeId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch special fee");
      }

      const data = await response.json();

      // Populate form with existing data
      const agentsLimited =
        Array.isArray(data.limit_to_agents) && data.limit_to_agents.length > 0;
      const brokeragesLimited =
        Array.isArray(data.limit_to_brokerages) && data.limit_to_brokerages.length > 0;
      const groupsLimited =
        Array.isArray(data.limit_to_groups) && data.limit_to_groups.length > 0;

      formik.setValues({
        name: data.name || "",
        limitTo: agentsLimited ? "agent" : brokeragesLimited ? "brokerage" : "none",
        limitToGroups: groupsLimited ? "groups" : "none",
        amount: data.amount?.toString() || "",
        taxable: data.taxable || false,
        tax_rule_id: data.tax_rule_id || "",                 // "group-<id>" | "rule-<id>" | ""
        tax_group: data.tax_group || null,        // full group object
        tax_rule: data.tax_rule || null,          // full tax rule object
        availableOn: (() => {
          const pages = (data.available_on || []).map((v: string) => String(v || "").trim().toLowerCase());
          const hasCart = pages.includes("cart");
          const hasCheckout = pages.includes("checkout") || pages.includes("checkoutpage");
          if (hasCart && hasCheckout) return "both Cart and Checkout";
          if (hasCheckout) return "checkout";
          if (hasCart) return "cart";
          return "";
        })(),
        status: data.status !== undefined ? data.status : true,
        required: data.required || false,
        selectedAgents: (data.limit_to_agents || []).map(String).filter(Boolean),
        selectedBrokerages: (data.limit_to_brokerages || []).map(String).filter(Boolean),
        selectedGroups: (data.limit_to_groups || []).map(String).filter(Boolean),
      });

      // Pre-fill Tax Rule when editing/viewing
      if (data?.tax_group?.id) {
        formik.setFieldValue("tax_rule_id", `group-${data.tax_group.id}`);
        formik.setFieldValue("tax_group", data.tax_group);
        formik.setFieldValue("tax_rule", null);
      } else if (data?.tax_rule?._id) {
        formik.setFieldValue("tax_rule_id", `rule-${data.tax_rule._id}`);
        formik.setFieldValue("tax_group", null);
        formik.setFieldValue("tax_rule", data.tax_rule);
      } else {
        formik.setFieldValue("tax_rule_id", "");
        formik.setFieldValue("tax_group", null);
        formik.setFieldValue("tax_rule", null);
      }


      // If there are selected agents, load their data for chip display (users-created-or-linked)
      if (data.limit_to_agents?.length > 0) {
        const orgId = effectiveOrganizationId;
        try {
          const needed = new Set((data.limit_to_agents || []).map((id: string) => String(id)));
          const byId = new Map<string, Agent>();
          const RESOLVE_PAGE_SIZE = 20;

          if (orgId) {
            const url = buildAgentsUsersCreatedOrLinkedUrl(orgId, 1, RESOLVE_PAGE_SIZE, "");

            const response = await fetch(url, {
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
              },
            });

            if (response.ok) {
              const json = await response.json();
              const list = Array.isArray(json) ? json : json.data ?? json.results ?? [];

              for (const u of list) {
                const agent = mapUsersCreatedOrLinkedRowToAgent(u);
                if (needed.has(agent.id)) {
                  byId.set(agent.id, agent);
                  needed.delete(agent.id);
                }
              }
            }
          }

          const selectedAgentsData: Agent[] = (data.limit_to_agents || []).map((id: string) => {
            const sid = String(id);
            return (
              byId.get(sid) || {
                id: sid,
                firstName: "",
                lastName: "",
                email: "",
                phone1: "",
              }
            );
          });
          setAgents(selectedAgentsData);
        } catch (err) {
          console.error("Failed to load selected agents:", err);
        }
      }

      // If there are selected brokerages, load their option data for chip display
      if (data.limit_to_brokerages?.length > 0) {
        try {
          // Load all brokerages for this signage firm
          const params = new URLSearchParams({
            licenseType: "BROKERAGE_FIRM",
            signageFirmID: effectiveOrganizationId || "",
            page: "1",
            limit: "100" // Load enough to find the selected ones
          });

          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/organization-profiles/search?${params.toString()}`,
            {
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.ok) {
            const brokerageData = await response.json();
            const brokerageList = Array.isArray(brokerageData) ? brokerageData : brokerageData.data ?? brokerageData.results ?? [];

            // Map selected brokerage IDs to their data
            const selectedBrokerages: Brokerage[] = data.limit_to_brokerages
              .map((brokerageId: string) => {
                const sid = String(brokerageId);
                const brokerage = brokerageList.find((u: any) => {
                  const orgId = String(
                    u?.organization_id ?? u?.organizationId ?? normalizeId(u) ?? ""
                  );
                  return orgId === sid;
                });
                if (brokerage) {
                  const id = String(
                    brokerage?.organization_id ??
                      brokerage?.organizationId ??
                      normalizeId(brokerage) ??
                      ""
                  );
                  const name =
                    String(
                      brokerage?.firmDetails?.companyName ||
                        brokerage?.companyName ||
                        brokerage?.text ||
                        brokerage?.label ||
                        `${brokerage?.firstName || ""} ${brokerage?.lastName || ""}`.trim() ||
                        ""
                    ).trim() || "(no name)";
                  return {
                    id,
                    name,
                    firstName: brokerage?.firstName || "",
                    lastName: brokerage?.lastName || "",
                    email: brokerage?.email || "",
                    phone1: brokerage?.phone1 || "",
                    firmDetails: {
                      companyName:
                        String(
                          brokerage?.firmDetails?.companyName ||
                            brokerage?.companyName ||
                            name
                        ).trim() || name,
                    },
                  };
                }
                return null;
              })
              .filter((brokerage: Brokerage | null): brokerage is Brokerage => brokerage !== null);

            setBrokerages(selectedBrokerages);

            // Also populate AsyncPaginate options for proper display
            const selectedBrokerageOptions: BrokerageOption[] = selectedBrokerages.map(brokerage => ({
              value: brokerage.id,
              label:
                brokerage.name ||
                brokerage.firmDetails?.companyName ||
                `${brokerage.firstName} ${brokerage.lastName}`.trim() ||
                "(no name)",
              firstName: brokerage.firstName,
              lastName: brokerage.lastName,
              email: brokerage.email,
              phone1: brokerage.phone1,
              firmDetails: brokerage.firmDetails,
            }));
            setSelectedBrokerageOptions(selectedBrokerageOptions);
          }
        } catch (err) {
          console.error("Failed to load selected brokerages:", err);
        }
      }

    } catch (err) {
      console.error("Error fetching special fee:", err);
      setApiError(err instanceof Error ? err.message : "Failed to fetch special fee");
      showError("Failed to fetch special fee. Please try again.");
    } finally {
      setLoading(prev => ({ ...prev, fetch: false }));
    }
  };

  // Fetch special fee data when in edit or view mode
  useEffect(() => {
    if ((isEdit || isView) && feeId) {
      fetchSpecialFee(feeId);
    }
  }, [isEdit, isView, feeId]);

  // Clear selected items when switching limitation types
  useEffect(() => {
    const currentLimitTo = formik.values.limitTo;

    // Clear agents when not limiting to agents
    if (currentLimitTo !== "agent" && formik.values.selectedAgents.length > 0) {
      formik.setFieldValue("selectedAgents", []);
      setAgents([]);
    }

    // Clear brokerages when not limiting to brokerages
    if (currentLimitTo !== "brokerage" && formik.values.selectedBrokerages.length > 0) {
      formik.setFieldValue("selectedBrokerages", []);
      setBrokerages([]);
      setSelectedBrokerageOptions([]);
    }
  }, [formik.values.limitTo]);

  // Clear selected groups when switching away from groups limitation
  useEffect(() => {
    const currentLimitToGroups = formik.values.limitToGroups;

    // Clear groups when not limiting to groups
    if (currentLimitToGroups !== "groups" && formik.values.selectedGroups.length > 0) {
      formik.setFieldValue("selectedGroups", []);
    }
  }, [formik.values.limitToGroups]);

  // Formik handles errors and submission state

  // Helper functions for multi-select
  const removeAgent = useCallback((agentId: string) => {
    formik.setFieldValue("selectedAgents", formik.values.selectedAgents.filter(id => id !== agentId));
    // Also remove from agents array for chip display
    setAgents(prev => prev.filter(agent => agent.id !== agentId));
  }, [formik.values.selectedAgents]);

  const removeBrokerage = useCallback((brokerageId: string) => {
    formik.setFieldValue("selectedBrokerages", formik.values.selectedBrokerages.filter(id => id !== brokerageId));
    // Also remove from local brokerages state
    setBrokerages(prev => prev.filter(brokerage => brokerage.id !== brokerageId));
    // Remove from AsyncPaginate options
    setSelectedBrokerageOptions(prev => prev.filter(option => option.value !== brokerageId));
  }, [formik.values.selectedBrokerages]);

  const removeGroup = useCallback((groupId: string) => {
    formik.setFieldValue("selectedGroups", formik.values.selectedGroups.filter(id => id !== groupId));
  }, [formik.values.selectedGroups]);

  // Simple component to display selected items
  const SelectedItemsDisplay = React.memo(({
    items,
    onRemove,
    title,
    data
  }: {
    items: string[],
    onRemove: (id: string) => void,
    title: string,
    data: Agent[] | Brokerage[] | Group[]
  }) => {
    if (items.length === 0) {
      return null;
    }

    return (
      <div className="mb-3">
        <div className="d-flex flex-wrap gap-2">
          {items.map((itemId) => {
            const item = data.find(d =>
              ('_id' in d && d._id === itemId) ||
              ('id' in d && d.id === itemId)
            );
            if (!item) return null;

            // Prefer explicit name/label, then company name, then person name
            const itemName =
              ("name" in item && String(item.name || "").trim()) ||
              ("label" in item && String((item as any).label || "").trim()) ||
              ("firmDetails" in item &&
                String(item.firmDetails?.companyName || "").trim()) ||
              `${(item as any).firstName || ""} ${(item as any).lastName || ""}`.trim() ||
              "(no name)";

            return (
              <div
                key={itemId}
                className="d-flex align-items-center gap-1 px-3 py-2"
                style={{
                  backgroundColor: '#e8f0ff',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                <span style={{ color: '#155dfc' }}>{itemName}</span>
                {!isDisabled && (
                  <button
                    type="button"
                    className="btn btn-sm p-0"
                    style={{
                      width: '18px',
                      height: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#155dfc',
                      border: 'none',
                      background: 'none',
                      borderRadius: '50%',
                      transition: 'background-color 0.2s ease'
                    }}
                    onClick={() => onRemove(itemId)}
                    title={`Remove ${itemName}`}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  });

  // controlled values
  const limitToValue = limitToOptions.find((o) => o.value === formik.values.limitTo) || null;
  const limitToGroupsValue = limitToGroupsOptions.find((o) => o.value === formik.values.limitToGroups) || null;
  const availableOnValue = formik.values.availableOn ? availableOnOptions.find((o) => o.value === formik.values.availableOn) ?? null : null;

  /** Agent/brokerage mode and group mode are mutually exclusive in the UI. */
  const limitToLocksGroups =
    formik.values.limitTo === "agent" || formik.values.limitTo === "brokerage";
  const groupsLockLimitTo = formik.values.limitToGroups === "groups";

  // Formik handles validation and submission automatically


  // ----- Component body -----
  const [nameErrBelowCol] = useState(false); // only for placement parity

  return (
    <>
      <div className="container sf-special-fee">
        <style>{styles}</style>

        <div className="row justify-content-center">
          <div className="col-12 col-lg-8">
            <div className="box-shadow-datatables card shadow-border-1 mb-3">
              <div className="card-body sf-card-body">
                {/* Header */}
                <div className="d-flex align-items-center gap-2 mb-2">
                  <div className="sf-header-icon" style={{ background: COLORS.headerBg }}>
                    <DollarSign size={18} style={{ color: COLORS.headerIcon }} />
                  </div>
                  <h4 className="mb-0">
                    {isView ? "View Special Fee" : isEdit ? "Edit Special Fee" : "Add Special Fee"}
                  </h4>
                </div>

                {loading.fetch ? (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="ms-2">Loading special fee data...</span>
                  </div>
                ) : (
                  <form onSubmit={formik.handleSubmit} className="mt-5">
                    {/* Basic Information */}
                    <div className="d-flex align-items-center gap-2 pb-2">
                      <div className="sf-section-icon" style={{ background: COLORS.basicBg }}>
                        <Info size={16} style={{ color: COLORS.basicIcon }} />
                      </div>
                      <h6 className="mb-0 fw-semibold">Basic Information</h6>
                    </div>

                    <div className="border-bottom mt-4 pb-4">
                      <div className="col-12 col-lg-6">
                        <label className="form-label">
                          <span className="d-inline-flex align-items-center gap-2">
                            {/* <Tag size={16} style={{ color: COLORS.feeIcon }} /> */}
                            Name <span className="text-danger">*</span>
                          </span>
                        </label>
                        <input
                          type="text"
                          className={`sf-form-control ${formik.touched.name && formik.errors.name ? "is-invalid" : ""}`}
                          placeholder="Enter special fee name"
                          disabled={isDisabled}
                          {...formik.getFieldProps("name")}
                        />
                        {formik.touched.name && formik.errors.name && (
                          <div className="invalid-feedback d-block">{formik.errors.name}</div>
                        )}
                      </div>
                    </div>

                    <div className="sf-divider my-3" />

                    {/* Configuration */}
                    <div className="d-flex align-items-center gap-2 pb-2 mt-4">
                      {/* <div className="sf-section-icon" style={{ background: COLORS.configBg }}>
                        <DollarSign size={16} style={{ color: COLORS.configIcon }} />
                      </div> */}
                      <h6 className="mb-0 fw-semibold">Configuration</h6>
                    </div>

                    {/* Limit to */}
                    <div className="mb-3 mt-4" role="group" aria-labelledby="limitToLabel">
                      <label id="limitToLabel" className="form-label d-flex align-items-center gap-2">
                        <Users size={16} style={{ color: "#f54a00" }} />
                        Limit to
                      </label>
                      <div className="col-12 col-lg-6">
                        <Select
                          classNamePrefix="react-select rs"
                          placeholder="Select limitation"
                          options={limitToOptions}
                          value={limitToValue}
                          onChange={(opt: any) => {
                            const newValue = opt?.value || "none";
                            const currentValue = formik.values.limitTo;

                            if (newValue === "agent" || newValue === "brokerage") {
                              formik.setFieldValue("limitToGroups", "none");
                              formik.setFieldValue("selectedGroups", []);
                            }

                            // Clear selected items when switching between agent and brokerage
                            if (currentValue === "agent" && newValue === "brokerage") {
                              formik.setFieldValue("selectedAgents", []);
                            } else if (currentValue === "brokerage" && newValue === "agent") {
                              formik.setFieldValue("selectedBrokerages", []);
                              setSelectedBrokerageOptions([]);
                              setBrokerages([]);
                            }

                            formik.setFieldValue("limitTo", newValue);
                          }}
                          onBlur={formik.handleBlur}
                          components={{ DropdownIndicator }}
                          styles={customSelectStyles}
                          isDisabled={isDisabled || groupsLockLimitTo}
                        />
                      </div>
                    </div>

                    {/* Agents Multi-Select */}
                    {formik.values.limitTo === "agent" && (
                      <div className="mb-3">
                        <label className="form-label d-flex align-items-center gap-2">
                          <Users size={16} style={{ color: COLORS.basicIcon }} />
                          Select Agents
                        </label>

                        {/* Selected items appear between label and dropdown */}
                        <SelectedItemsDisplay
                          items={formik.values.selectedAgents}
                          onRemove={removeAgent}
                          title="Agents"
                          data={agents}
                        />

                        <div className="col-12 col-lg-8">
                          <AsyncPaginate
                            key={`special-fee-agents-${effectiveOrganizationId}`}
                            classNamePrefix="react-select rs"
                            placeholder="Search and select agents..."
                            loadOptions={loadAgentOptions}
                            value={null}
                            onChange={(option: AgentOption | null) => {
                              if (option && !formik.values.selectedAgents.includes(option.value)) {
                                // Add single agent
                                formik.setFieldValue("selectedAgents", [...formik.values.selectedAgents, option.value]);
                                // Also add to agents array for chip display
                                setAgents(prev => {
                                  if (!prev.find(a => a.id === option.value)) {
                                    return [...prev, {
                                      id: option.value,
                                      firstName: option.firstName,
                                      lastName: option.lastName,
                                      email: option.email,
                                      phone1: option.phone1,
                                    }];
                                  }
                                  return prev;
                                });
                              }
                            }}
                            additional={{ page: 1 }}
                            defaultOptions
                            cacheUniqs={[effectiveOrganizationId, formik.values.limitTo]}
                            debounceTimeout={300}
                            isClearable
                            isDisabled={isDisabled || groupsLockLimitTo}
                            components={{
                              DropdownIndicator,
                              Option: (props: any) => (
                                <CustomOption
                                  {...props}
                                  selectProps={{
                                    ...props.selectProps,
                                    selectedValues: formik.values.selectedAgents,
                                    onSelectionChange: (newSelected: string[], optionData?: AgentOption, isAdding?: boolean) => {
                                      formik.setFieldValue("selectedAgents", newSelected);
                                      // Sync agents array based on the change
                                      if (optionData && isAdding !== undefined) {
                                        if (isAdding) {
                                          // Adding: add agent to agents array if not already present
                                          setAgents(prev => {
                                            if (!prev.find(a => a.id === optionData.value)) {
                                              return [...prev, {
                                                id: optionData.value,
                                                firstName: optionData.firstName,
                                                lastName: optionData.lastName,
                                                email: optionData.email,
                                                phone1: optionData.phone1,
                                              }];
                                            }
                                            return prev;
                                          });
                                        } else {
                                          // Removing: remove agent from agents array
                                          setAgents(prev => prev.filter(agent => agent.id !== optionData.value));
                                        }
                                      } else {
                                        // Fallback: sync agents array to match selectedAgents
                                        setAgents(prev => prev.filter(agent => newSelected.includes(agent.id)));
                                      }
                                    }
                                  }}
                                />
                              )
                            }}
                            styles={customSelectStyles}
                            getOptionValue={(option: AgentOption) => option.value}
                            getOptionLabel={(option: AgentOption) => option.label}
                            formatOptionLabel={renderSignagePersonOptionLabel}
                            isOptionSelected={(option: AgentOption) => formik.values.selectedAgents.includes(option.value)}
                          />
                        </div>
                      </div>
                    )}

                    {/* Brokerages Multi-Select */}
                    {formik.values.limitTo === "brokerage" && (
                      <div className="mb-3">
                        <label className="form-label d-flex align-items-center gap-2">
                          <Building2 size={16} style={{ color: COLORS.configIcon }} />
                          Select Brokerages
                        </label>

                        {/* Selected items appear between label and dropdown */}
                        <SelectedItemsDisplay
                          items={formik.values.selectedBrokerages}
                          onRemove={removeBrokerage}
                          title="Brokerages"
                          data={brokerages}
                        />

                        <div className="col-12 col-lg-8">
                          <AsyncPaginate
                            key={`special-fee-brokerages-${effectiveOrganizationId}`}
                            classNamePrefix="react-select rs"
                            placeholder="Search and select brokerages..."
                            loadOptions={loadBrokerageOptions}
                            value={selectedBrokerageOptions}
                            onChange={(selected: MultiValue<BrokerageOption>) => {
                              // console.log("Selected brokerages:", selected);
                              const selectedArray = selected || [];

                              // Update AsyncPaginate state
                              setSelectedBrokerageOptions([...selectedArray]);

                              // Update Formik with just the IDs
                              const selectedIds = selectedArray.map(option => option.value);
                              formik.setFieldValue("selectedBrokerages", selectedIds);

                              // Update local brokerages state for chip display
                              const brokeragesToStore: Brokerage[] = selectedArray.map(option => ({
                                id: option.value,
                                name: option.label,
                                firstName: option.firstName,
                                lastName: option.lastName,
                                email: option.email,
                                phone1: option.phone1,
                                firmDetails: {
                                  companyName:
                                    option.firmDetails?.companyName || option.label || "",
                                },
                              }));
                              setBrokerages(brokeragesToStore);

                              // console.log("Brokerages updated:", { selectedIds, brokeragesToStore });
                            }}
                            additional={{ page: 1 }}
                            defaultOptions={true}
                            cacheUniqs={[effectiveOrganizationId, formik.values.limitTo]}
                            debounceTimeout={300}
                            isMulti
                            isClearable
                            isDisabled={isDisabled || groupsLockLimitTo}
                            getOptionValue={(option) => option.value}
                            getOptionLabel={(option) => option.label}
                            formatOptionLabel={renderSignagePersonOptionLabel}
                            closeMenuOnSelect={false}
                            hideSelectedOptions={true}
                            components={{
                              DropdownIndicator
                            }}
                            styles={customSelectStyles}
                          />
                        </div>
                      </div>
                    )}

                    {/* Limit to Groups */}
                    <div className="mb-3 pb-1">
                      <label className="form-label d-flex align-items-center gap-2">
                        <Tag size={16} style={{ color: COLORS.feeIcon }} />
                        Limit to Groups
                      </label>
                      <div className="col-12 col-lg-6">
                        <Select
                          classNamePrefix="react-select rs"
                          placeholder="No groups limitation"
                          options={limitToGroupsOptions}
                          value={limitToGroupsValue}
                          onChange={(opt: any) => {
                            const newValue = opt?.value || "none";
                            if (newValue === "groups") {
                              formik.setFieldValue("limitTo", "none");
                              formik.setFieldValue("selectedAgents", []);
                              formik.setFieldValue("selectedBrokerages", []);
                              setAgents([]);
                              setBrokerages([]);
                              setSelectedBrokerageOptions([]);
                            }
                            formik.setFieldValue("limitToGroups", newValue);
                          }}
                          onBlur={formik.handleBlur}
                          components={{ DropdownIndicator }}
                          styles={customSelectStyles}
                          isDisabled={isDisabled || limitToLocksGroups}
                        />
                      </div>
                    </div>

                    {/* Groups Multi-Select */}
                    {formik.values.limitToGroups === "groups" && (
                      <div className="mb-3">
                        <label className="form-label d-flex align-items-center gap-2">
                          {/* <Package size={16} style={{ color: COLORS.feeIcon }} /> */}
                          Select Groups
                        </label>

                        {/* Selected items appear between label and dropdown */}
                        <SelectedItemsDisplay
                          items={formik.values.selectedGroups}
                          onRemove={removeGroup}
                          title="Groups"
                          data={groups}
                        />

                        <div className="col-12 col-lg-8">
                          <Select
                            classNamePrefix="react-select rs"
                            placeholder="Search and select groups..."
                            options={[
                              { value: "", label: "-- Select --" },
                              ...(groups || []).map((group) => ({
                                value: group._id,
                                label: group.name,
                              }))
                            ]}
                            value={null}
                            onChange={(option: any) => {
                              if (option && option.value && !formik.values.selectedGroups.includes(option.value)) {
                                formik.setFieldValue("selectedGroups", [...formik.values.selectedGroups, option.value]);
                              }
                            }}
                            components={{
                              DropdownIndicator,
                              Option: (props) => (
                                <CustomOption
                                  {...props}
                                  selectProps={{
                                    ...props.selectProps,
                                    selectedValues: formik.values.selectedGroups,
                                    onSelectionChange: (newSelected: string[]) => {
                                      formik.setFieldValue("selectedGroups", newSelected);
                                    }
                                  }}
                                />
                              )
                            }}
                            styles={customSelectStyles}
                            isClearable
                            isLoading={loading.groups}
                            noOptionsMessage={() => loading.groups ? "Loading..." : "No groups found"}
                            isDisabled={isDisabled || limitToLocksGroups}
                            getOptionValue={(option) => option.value}
                            isOptionSelected={(option) => formik.values.selectedGroups.includes(option.value)}
                          />
                        </div>
                      </div>
                    )}

                    <div className="sf-divider my-3" />

                    {/* Fee Details */}
                    <div className="d-flex align-items-center gap-2 pb-2 mt-4">
                      <div className="sf-section-icon" style={{ background: COLORS.feeBg }}>
                        <DollarSign size={16} style={{ color: COLORS.feeIcon }} />
                      </div>
                      <h6 className="mb-0 fw-semibold">Fee Details</h6>
                    </div>

                    <div className="row g-3 mt-3">
                      <div className="col-12 col-lg-6">
                        <label className="form-label d-flex align-items-center gap-2">
                          <DollarSign size={16} style={{ color: COLORS.configIcon }} />
                          Amount <span className="text-danger">*</span>
                        </label>
                        <div className="input-group gap-2">
                          <span className="br-8 input-group-text ps-3 sf-input-prefix">$</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            className={`sf-amount-input ${formik.touched.amount && formik.errors.amount ? "is-invalid" : ""}`}
                            placeholder="0.00"
                            disabled={isDisabled}
                            name="amount"
                            value={formik.values.amount}
                            onBlur={formik.handleBlur}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^\d*\.?\d*$/.test(value)) {
                                formik.setFieldValue("amount", value);
                              }
                            }}
                          />
                        </div>
                        {formik.touched.amount && formik.errors.amount && (
                          <div className="invalid-feedback d-block">{formik.errors.amount}</div>
                        )}
                      </div>

                      <div className="col-12 col-lg-6">
                        <label className="form-label">Taxable</label>
                        <div className="d-flex align-items-center gap-2">
                          <div className="form-check form-switch m-0">
                            <input
                              className="form-check-input check"
                              type="checkbox"
                              role="switch"
                              id="taxableSwitch"
                              checked={formik.values.taxable}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                formik.setFieldValue("taxable", checked);
                                if (!checked) {
                                  // clear when turning OFF
                                  formik.setFieldValue("tax_rule_id", "");
                                  formik.setFieldValue("tax_group", null);
                                  formik.setFieldValue("tax_rule", null);
                                }
                              }}
                              // onChange={(e) => formik.setFieldValue("taxable", e.target.checked)}
                              disabled={isDisabled}
                            />
                          </div>
                          <span className="sf-help">{formik.values.taxable ? "Taxable" : "Not Taxable"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="row g-3 mt-3">
                      <div className="col-md-6">
                        {/* Add/Edit: show selector only when taxable is ON */}
                        {!isView && (
                          <TaxRuleSelect
                            token={token || ""}                // your API token
                            taxable={formik.values.taxable}    // controls visibility
                            formik={formik}                    // lets the component write into Formik
                            taxRuleIdName="tax_rule_id"
                            groupFieldName="tax_group"
                            ruleFieldName="tax_rule"
                            wrapperClassName="d-flex flex-column ms-1"
                            selectClassName="form-select"
                            label="Tax Rule"
                          />
                        )}

                        {/* View: read-only display when taxable is ON */}
                        {isView && formik.values.taxable && (
                          <div className="d-flex flex-column ms-1" style={{ minWidth: 300 }}>
                            <label className="form-label mb-1">Tax Rule</label>
                            <div
                              className="form-control"
                              style={{ background: "#f5f6fa", pointerEvents: "none" }}
                            >
                              {formik.values.tax_group?.name ||
                                (formik.values.tax_rule
                                  ? `${formik.values.tax_rule.country} - ${formik.values.tax_rule.taxRate}%`
                                  : "—")}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 pb-4 border-bottom">
                      <label className="form-label d-flex align-items-center gap-2">
                        {/* <Package size={16} style={{ color: COLORS.basicIcon }} /> */}
                        Available on <span className="text-danger">*</span>
                      </label>
                      <div className="col-12 col-lg-6">
                        <Select
                          classNamePrefix="react-select rs"
                          placeholder="Select availability..."
                          options={availableOnOptions}
                          value={availableOnValue}
                          onChange={(opt: any) => formik.setFieldValue("availableOn", opt?.value || "")}
                          onBlur={formik.handleBlur}
                          components={{ DropdownIndicator }}
                          styles={customSelectStyles}
                          isDisabled={isDisabled}
                        />
                        {formik.touched.availableOn && formik.errors.availableOn && (
                          <div className="invalid-feedback d-block">{formik.errors.availableOn}</div>
                        )}
                      </div>
                    </div>

                    <div className="sf-divider my-3" />

                    {/* Status & Requirements */}
                    <div className="d-flex align-items-center gap-2 pb-2 mt-4">
                      <div className="sf-section-icon" style={{ background: COLORS.statusBg }}>
                        <Info size={16} style={{ color: COLORS.statusIcon }} />
                      </div>
                      <h6 className="mb-0 fw-semibold">Status & Requirements</h6>
                    </div>

                    <div className="row g-3 mt-3">
                      <div className="col-6 col-lg-6">
                        <label className="form-label">Status</label>
                        <div className="d-flex align-items-center gap-2">
                          <div className="form-check form-switch m-0">
                            <input
                              className="form-check-input check"
                              type="checkbox"
                              role="switch"
                              id="statusSwitch"
                              checked={formik.values.status}
                              onChange={(e) => formik.setFieldValue("status", e.target.checked)}
                              disabled={isDisabled}
                            />
                          </div>
                          <span className="text-body-secondary">{formik.values.status ? "Active" : "Inactive"}</span>
                        </div>
                      </div>

                      <div className="col-6 col-lg-6">
                        <label className="form-label">Required</label>
                        <div className="d-flex align-items-center gap-2">
                          <div className="form-check form-switch m-0">
                            <input
                              className="form-check-input check"
                              type="checkbox"
                              role="switch"
                              id="requiredSwitch"
                              checked={formik.values.required}
                              onChange={(e) => formik.setFieldValue("required", e.target.checked)}
                              disabled={isDisabled}
                            />
                          </div>
                          <span className="text-body-secondary">{formik.values.required ? "Yes" : "No"}</span>
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="sf-divider my-3 border-top mt-4" />
                    {!isView && (
                      <div className="d-flex justify-content-between align-items-center py-2">
                        <button type="button" onClick={() => navigate(-1)} className="btn back-btn surface-btn fw-bold fs-16">
                          <i className="bi bi-chevron-left me-2" /> Back
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary save-btn surface-btn fw-bold fs-16 px-4"
                          disabled={formik.isSubmitting}
                        >
                          {formik.isSubmitting ? "Saving..." : (isEdit ? "Update Fee" : "Add Fee")}
                        </button>
                      </div>
                    )}
                  </form>
                )}
              </div>

              {/* Formik handles submission automatically */}
            </div>
          </div>
        </div>
      </div >
    </>
  );
}